
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Eye, FileText, Folder, MoreHorizontal } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useToast } from '@/hooks/use-toast';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { useFeedbacksFetcher, FeedbackData } from '@/hooks/useFeedbacksFetcher';

interface ProjectInfo {
  id: string;
  name: string;
}

interface FeedbackWithProject extends FeedbackData {
  project?: ProjectInfo | null;
}

interface GivenFeedbacksProps {
  isCandidat?: boolean;
  isEntreprise?: boolean;
}

export const GivenFeedbacks: React.FC<GivenFeedbacksProps> = ({ isCandidat, isEntreprise }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [feedbacks, setFeedbacks] = useState<FeedbackWithProject[]>([]);
  const [loading, setLoading] = useState(true);

  // Load feedbacks given by the user
  useEffect(() => {
    const fetchGivenFeedbacks = async () => {
      if (!user) return;
      
      setLoading(true);
      try {
        // Fetch feedbacks created by this user
        const { data, error } = await supabase
          .from('feedbacks')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });
          
        if (error) throw error;
        
        // Get project information for each feedback
        const feedbacksWithProjects = await Promise.all((data || []).map(async (feedback) => {
          try {
            // Check if this feedback is associated with a project
            const { data: projectData, error: projectError } = await supabase
              .from('projects_feedbacks')
              .select(`
                project_id,
                projects (id, name)
              `)
              .eq('feedback_id', feedback.id)
              .maybeSingle();
            
            if (projectError) throw projectError;
            
            return {
              ...feedback,
              project: projectData && projectData.projects ? {
                id: projectData.project_id,
                name: projectData.projects.name
              } : null
            };
          } catch (err) {
            console.error('Error fetching project info for feedback:', err);
            return { ...feedback, project: null };
          }
        }));
        
        setFeedbacks(feedbacksWithProjects);
      } catch (error: any) {
        console.error('Error loading given feedbacks:', error);
        toast({
          title: "Erreur",
          description: "Impossible de charger les feedbacks donnés",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchGivenFeedbacks();
  }, [user, toast]);

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd MMMM yyyy', { locale: fr });
    } catch (e) {
      return dateString;
    }
  };

  const handleViewFeedback = (feedbackId: string) => {
    navigate(`/view-feedback/${feedbackId}`);
  };

  const handleViewProject = (projectId: string) => {
    if (projectId) {
      navigate(`/projects/${projectId}`);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((_, i) => (
          <Card key={i} className="p-4">
            <Skeleton className="h-6 w-1/4 mb-2" />
            <Skeleton className="h-4 w-3/4 mb-1" />
            <Skeleton className="h-4 w-1/2" />
          </Card>
        ))}
      </div>
    );
  }

  if (feedbacks.length === 0) {
    return (
      <Card className="p-4 text-center">
        <p className="mb-4">Vous n'avez pas encore donné de feedback.</p>
        <Button onClick={() => navigate('/request')}>
          Créer un feedback
        </Button>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {feedbacks.map((feedback) => (
        <Card key={feedback.id} className="overflow-hidden">
          <div className="p-4">
            <div className="flex justify-between items-start mb-2">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-medium text-lg">
                    À: {feedback.candidate_name}
                  </h3>
                  {feedback.certified && (
                    <Badge className="bg-green-100 text-green-800 text-xs">
                      Certifié
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-gray-500">{feedback.job_title}</p>
              </div>

              <div className="flex flex-col items-end">
                <div className="text-sm text-gray-500">
                  {formatDate(feedback.created_at)}
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleViewFeedback(feedback.id)}>
                      <Eye className="mr-2 h-4 w-4" />
                      Voir le feedback
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {feedback.project && (
              <div className="mt-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleViewProject(feedback.project!.id)}
                  className="flex items-center gap-1 text-xs"
                >
                  <Folder className="h-3 w-3" />
                  <span>Projet: {feedback.project.name}</span>
                </Button>
              </div>
            )}

            <div className="flex justify-end mt-4">
              <Button 
                size="sm" 
                onClick={() => handleViewFeedback(feedback.id)}
              >
                Voir le feedback
              </Button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};

export default GivenFeedbacks;
